import type { Enum } from '../../types/Enum'

/**
 * Enumerates the types of transport-level events that can occur within an event bus
 * implementation. These are emitted to indicate changes in connection state or
 * transport errors.
 */
export const EventBusTransportEventTypes = {
  STATE: 'state',
  ERROR: 'error',
} as const

export type EventBusTransportEventTypeEnum = Enum<
  typeof EventBusTransportEventTypes
>

/**
 * Constants for EventBus connection states.
 * These map directly to the underlying transport's lifecycle.
 */
export const EventBusConnectionStates = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
} as const

/**
 * Represents the possible states of an event bus connection.
 * Derived from EventBusConnectionStates constants.
 */
export type EventBusConnectionStateEnum = Enum<typeof EventBusConnectionStates>

/**
 * Constants for EventBus transport error codes.
 * Implementations can normalize provider-specific codes to one of these.
 */
export const EventBusTransportErrorCodes = {
  AUTH_ERROR: 'auth-error',
  CONNECTION_ERROR: 'connection-error',
  UNKNOWN_ERROR: 'unknown',
} as const

/**
 * Represents categories of transport-level errors.
 * Derived from EventBusTransportErrorCodes constants.
 */
export type EventBusTransportErrorCodeEnum = Enum<
  typeof EventBusTransportErrorCodes
>

/**
 * Payload describing a change in transport connection state.
 */
export interface EventBusTransportState {
  /** The current connection state. */
  state: EventBusConnectionStateEnum

  /**
   * Optional human-readable message describing why the state change
   * occurred (e.g., 'network down', 'server unavailable').
   */
  reason?: string
}

/**
 * Represents a transport-level error from the EventBus system.
 * This type can be used by consumers when declaring error handling callbacks.
 * All EventBus implementations should return transport errors in this format.
 */
export interface EventBusTransportError {
  /** The normalized transport error code category */
  code: EventBusTransportErrorCodeEnum
  /** Optional human-readable error message */
  message?: string
  /** The original error object that caused this normalized error */
  cause?: unknown
}

/**
 * Event map defining built-in transport-level events supported
 * by all event bus implementations.
 */
export type EventBusTransportEventMap = {
  [EventBusTransportEventTypes.STATE]: EventBusTransportState
  [EventBusTransportEventTypes.ERROR]: EventBusTransportError
}

/**
 * Generic constraint for domain-specific event maps that define event names and their
 * data types.
 *
 * This type explicitly excludes transport event keys ('state', 'error') to prevent
 * conflicts and enable proper type inference when combining with transport events.
 * This ensures that (T & EventBusTransportEventMap)[K] correctly resolves to
 * EventBusTransportEventMap[K] when K is a transport event key.
 *
 * This type enables type-safe event handling while allowing domain modules to define
 * their own event contracts.
 */
export type EventBusDomainEventMap = Omit<
  Record<string, unknown>,
  keyof EventBusTransportEventMap
>

/**
 * Utility type that combines a domain event map with transport events.
 * This represents the complete set of events available on an event bus.
 *
 * This type is used internally by IEventBus methods (bind, unbind) to represent
 * the union of domain events (from T) and transport events (state, error).
 */
export type EventBusEventMap<T extends EventBusDomainEventMap> = T &
  EventBusTransportEventMap

/**
 * Utility type to extract valid event keys from a combined domain and transport event map.
 *
 * Returns a union type of all valid event keys: domain event keys from T plus
 * transport event keys ('state', 'error'). This type is used as a constraint in
 * IEventBus method signatures to ensure type-safe event key usage.
 */
export type EventBusEventKey<T extends EventBusDomainEventMap> =
  keyof EventBusEventMap<T>

/**
 * Utility type that transforms a domain event map into a map of handler functions.
 * Each event handler receives the corresponding event data as its parameter.
 *
 * This type is used to define type-safe event handler maps, where each handler
 * function signature matches its corresponding event data type.
 */
export type EventBusEventHandlerMap<T extends EventBusDomainEventMap> = {
  [K in keyof T]: (event: T[K]) => void
}

/**
 * Generic interface for event-driven message bus implementations.
 *
 * This interface abstracts provider-specific real-time messaging SDKs (e.g., Pusher,
 * SocketIO), which themselves use underlying transport mechanisms like WebSockets or
 * Server-Side Events.
 *
 * It exposes a consistent event-driven contract for subscribing to both domain and
 * transport-level events. Transport events ('state', 'error') are automatically
 * available on all event bus instances, in addition to the domain events defined by T.
 *
 * @template T - A domain event map defining event names and their corresponding data types.
 *               Transport events are automatically included and do not need to be specified.
 */
export interface IEventBus<
  T extends EventBusDomainEventMap = EventBusDomainEventMap,
> {
  /**
   * Establishes a connection to the underlying transport.
   * Should be idempotent - multiple calls should not create multiple connections.
   */
  connect(): void

  /**
   * Closes the connection and releases all underlying resources.
   * Should be safe to call even if not currently connected.
   */
  disconnect(): void

  /**
   * Registers a callback for a given event.
   *
   * @param event - The event name or transport event type.
   * @param callback - The function to be invoked when the event occurs.
   */
  bind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void

  /**
   * Unregisters a specific callback from a given event.
   *
   * @param event - The event name or transport event type.
   * @param callback - The function to be removed.
   */
  unbind<K extends EventBusEventKey<T>>(
    event: K,
    callback: (data: EventBusEventMap<T>[K]) => void
  ): void

  /**
   * Unregisters all callbacks for a specific event, or all events if none is specified.
   *
   * @param event - (Optional) Event name or transport event type.
   */
  unbindAll(event?: EventBusEventKey<T>): void
}
